import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { Typography, Card, CardContent } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';

import * as api from '_core/utils/api';
import { useVisitorInfo } from '_core/utils/visitorInfo';
import { withLang } from '_core/hocs/withLang';
import { withCatch } from '_core/hocs/withCatch';
import HtmlView from '_core/components/HtmlView';
import { ContentBreadcrumbs } from '_core/components/ContentBreadcrumbs';
import { State } from '_core/components/State';
import { Title } from '_core/components/Title';
import Video from '_core/components/Video';

import { articleStyle } from './articleStyle';


const RawArticle = (props) => {
  const {
    classes,
    id,
    path,
    hasDate,
    breadcrumbs,
    inCard,
    pathPrefix,
    render,
    renderEmpty,
    renderFailure,
    renderProgress,
    emptyMessage,
    stateProps,
    langInfo,
    getLangString: l
  } = props;
  
  
  const visitorInfo = useVisitorInfo();
  
  const [nodeKey, setNodeKey] = React.useState(0);
  
  React.useEffect(() => {
    setNodeKey((nk) => nk + 1);
  }, [langInfo?.id, visitorInfo.info]);
  
  if (!nodeKey || !visitorInfo || visitorInfo.isLoading)
    return null;
  
  const getArticle = async () => {
    const param = {
      lang: langInfo.id,
      fields: 'id,time,path,name,title,description,content,video',
      visitor_group: visitorInfo.info?.visitor_group?.join(',')
    };
    
    if (path) {
      const pathTrim = path.replace(/^(\/|\/$)/, '');
      
      const index = pathTrim.lastIndexOf('/');
      
      if (index < 0) {
        param.name = pathTrim;
      } else {
        param.path = pathTrim.substring(0, index);
        param.name = pathTrim.substring(index + 1);
      }
    } else {
      param.id = id;
    }
    
    const list = await api.getPortalArticle(param);
    
    if (!list?.length)
      throw new Error('Article not found');
    
    if (breadcrumbs) {
      const pathSplit = (path.startsWith(pathPrefix + '/') ? path.substring(pathPrefix.length + 1) : path).split('/');
      
      pathSplit.pop();
      
      let breadcrumbsPath = pathPrefix;
      
      list[0].path = await Promise.all(pathSplit.map((name) => {
        const result = api.getPortalArticle({ lang: langInfo.id, path: breadcrumbsPath, name, fields: 'title' }).then((data) => ({ name, title: data[0] || name }));
        
        breadcrumbsPath += '/' + name;
        
        return result;
      }));
    }
    
    return list[0];
  };
  
  
  const renderHeader = (article) => {
    const title = l(article.title);
    
    return (
      <>
        {hasDate ? (
          <Typography variant="caption" className={classes.date}>
            {moment(article.time).format('DD.MM.YYYY')}
          </Typography>
        ) : null}
  
        {title ? (
          <Title textProps={{ variant: 'h5' }} className={classes.title}>
            {title}
          </Title>
        ) : null}
      </>
    );
  };

  const renderEmptyMessage = () =>
    renderEmpty != null ? (
      renderEmpty()
    ) : (
      <Typography align="center" variant="h6" color="textSecondary">
        {emptyMessage != null ? l(emptyMessage) : l('r.nichego.ne.najdeno')}
      </Typography>
    )

  return (
    <State
      {...stateProps}
      key={nodeKey}
      getProps={{ article: getArticle }}
      render={
        render != null
          ? render
          : ({ article }) => {
              const description = l(article.description);
              const content = l(article.content);
              const video = l(article.video);
              
              return (
                <>
                  {breadcrumbs ? <ContentBreadcrumbs type="article" content={article} pathPrefix={pathPrefix} /> : null}
                  
                  {inCard ? (
                    <Card>
                      <CardContent className={classes.cardContent}>
                        {renderHeader(article)}
                        
                        {description ? (
                          <Typography variant="h6" className={classes.description}>
                            {description}
                          </Typography>
                        ) : null}
                        
                        {video ? (
                          <Video src={video} autoplay />
                        ) : null}
                        
                        {content ? (
                          <Typography className={classes.content}>
                            <HtmlView>
                              {content}
                            </HtmlView>
                          </Typography>
                        ) : null}
                      </CardContent>
                    </Card>
                  ) : (
                    <>
                      {renderHeader(article)}
                      
                      {description ? (
                        <Typography variant="h6" className={classes.description}>
                          {description}
                        </Typography>
                      ) : null}
                      
                      {video ? (
                        <Video src={video} autoplay />
                      ) : null}
                      
                      {content ? (
                        <Typography className={classes.content}>
                          <HtmlView>
                            {content}
                          </HtmlView>
                        </Typography>
                      ) : null}
                    </>
                  )}
                </>
              )
            }
      }
      renderFailure={
        renderFailure != null
          ? renderFailure
          : ({ error }) => renderEmptyMessage()
      }
      renderProgress={renderProgress}
    />
  )
}

RawArticle.defaultProps = {
  hasDate: false,
  breadcrumbs: false,
  inCard: true,
  pathPrefix: ''
}

RawArticle.propTypes = {
  // self props
  id: PropTypes.number,
  path: PropTypes.string,
  hasDate: PropTypes.bool.isRequired,
  breadcrumbs: PropTypes.bool.isRequired,
  inCard: PropTypes.bool.isRequired,
  pathPrefix: PropTypes.string.isRequired,
  emptyMessage: PropTypes.string,
  render: PropTypes.func,
  renderEmpty: PropTypes.func,
  renderFailure: PropTypes.func,
  renderProgress: PropTypes.func,
  stateProps: PropTypes.object,

  // `withStyles` HOC props
  classes: PropTypes.object.isRequired,

  // `withLang` HOC props
  langInfo: PropTypes.object,
  langStrings: PropTypes.object,
  getLangObject: PropTypes.func,
  getFirstLangString: PropTypes.func,
  getLangStringSet: PropTypes.func,
  getLangString: PropTypes.func.isRequired,
}

export const Article = withCatch(withLang(withStyles(articleStyle)(RawArticle)))
